import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Upload, ArrowLeft, Download, FileText, X } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { usePageFocus } from '@/hooks/usePageFocus';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';
import { companyService } from '@/services/companyService';
import { ROUTES } from '@/lib/routes';
import type { CategoryKey } from '@/lib/navigation';
import { decodeHtmlEntities } from '@/lib/utils';

interface ImportError { row: number; field: string; message: string; }
interface PreviewRow {
  order_id?: string;
  sku_id?: string;
  sale_quantity?: number;
  sale_unit_price?: number;
  order_datetime?: string;
  order_date?: string;
  order_time?: string;
  pickup_date?: string;
  pickup_time?: string;
  delivery_date?: string;
  delivery_time?: string;
  shipment_id?: string;
  sale_status?: string;
  delivery_mode?: string;
  warehouse_id?: string;
  warehouse_name?: string;
  sub_order_id?: string;
  sale_sub_category_code?: string;
  sale_sub_category_name?: string;
  sale_mix_match_promo_id?: string;
  mix_match_promo_name_en?: string;
  mix_match_promo_name_zh?: string;
  sale_sku_brand_en?: string;
  sale_sku_brand_zh?: string;
  sale_sku_name_en?: string;
  sale_sku_name_zh?: string;
  sku_spec?: string;
  store_credit_discount?: number;
  total_amount?: number;
  recipient_name?: string;
  contact_number?: string;
  delivery_address?: string;
  require_removal_service?: string;
  delivery_district?: string;
  add_on_promotion?: string;
  [key: string]: any;
}

interface SalesImportEngineProps {
  file: File | null;
  rows: PreviewRow[] | null;
  startIndex?: number;
  onProgress: (index: number) => void;
  onComplete: () => void;
  onError?: (info: { message: string; failedBatch?: { type: 'update' | 'insert'; batchIndex: number; size: number } }) => void;
  onResult?: (info: { hardFailedRows: { excelRow: number; message: string }[] }) => void;
  isProcessing?: boolean;
  currentCompany: { id: string; code?: string; name?: string } | null;
  isPageFocusRecovery?: boolean;
}

const ORDER_CATEGORY: CategoryKey = 'order';

const SalesImport: React.FC = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // 父層狀態：檔案、進度、重掛載控制
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [startIndex, setStartIndex] = useState(0);
  const [revision, setRevision] = useState(0);
  const rowsRef = useRef<PreviewRow[] | null>(null);
  const [previewData, setPreviewData] = useState<PreviewRow[]>([]);

  // UI 狀態
  const [errors, setErrors] = useState<ImportError[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number }>({ done: 0, total: 0 });
  const [failedRows, setFailedRows] = useState<{ excelRow: number; message: string }[]>([]);

  // 頁面焦點恢復處理
  const lastProgressRef = useRef<{ done: number; total: number }>({ done: 0, total: 0 });
  const lastActivityRef = useRef<number>(Date.now());

  // 匯入狀態持久化
  const IMPORT_STATE_KEY = 'sales_import_state';

  // 頁面焦點恢復保護標記
  const [isPageFocusRecovery, setIsPageFocusRecovery] = useState(false);

  const handlePageFocusRecovery = useCallback(async () => {
    console.log('🔄 SalesImport頁面焦點恢復');

    // 設置頁面焦點恢復保護標記
    setIsPageFocusRecovery(true);

    const now = Date.now();
    const timeSinceLastActivity = now - lastActivityRef.current;
    lastActivityRef.current = now;

    // 🔒 關鍵保護：如果正在處理中，絕對不做任何可能觸發重新渲染的操作
    if (isProcessing && progress.total > 0) {
      console.log('🔒 匯入正在進行中，頁面焦點恢復只保存狀態，不觸發任何操作');

      // 只保存狀態，不做任何其他操作
      const importState = {
        isProcessing: true,
        progress: { ...progress },
        startIndex,
        timestamp: now,
        file: selectedFile ? {
          name: selectedFile.name,
          size: selectedFile.size,
          type: selectedFile.type
        } : null
      };

      sessionStorage.setItem(IMPORT_STATE_KEY, JSON.stringify(importState));
      console.log('💾 已靜默保存匯入狀態到 sessionStorage');

      // 靜默更新追蹤狀態，不觸發重新渲染
      lastProgressRef.current = { ...progress };

      // 絕對不顯示toast，不改變任何狀態

      // 在短延遲後重置保護標記，確保匯入引擎不會被永久鎖定
      setTimeout(() => {
        setIsPageFocusRecovery(false);
      }, 100); // 100ms後清除，確保相關操作已完成

      return;
    }

    // 如果沒有進行中的匯入，檢查是否有保存的狀態
    const savedState = sessionStorage.getItem(IMPORT_STATE_KEY);
    if (savedState) {
      try {
        const importState = JSON.parse(savedState);
        const stateAge = now - importState.timestamp;

        // 如果狀態太舊（超過5分鐘），清除它
        if (stateAge > 5 * 60 * 1000) {
          sessionStorage.removeItem(IMPORT_STATE_KEY);
          console.log('🗑️ 清除過期的匯入狀態');
          return;
        }

        console.log('🔄 發現保存的匯入狀態，檢查是否需要恢復');
        // 這裡可以添加恢復邏輯，但現在先只記錄

      } catch (e) {
        console.error('❌ 解析保存的匯入狀態失敗:', e);
        sessionStorage.removeItem(IMPORT_STATE_KEY);
      }
    }

    // 清除頁面焦點恢復保護標記
    setTimeout(() => {
      setIsPageFocusRecovery(false);
    }, 100); // 100ms後清除，確保相關操作已完成

  }, [isProcessing, progress, startIndex, selectedFile]);

  // 使用頁面焦點恢復 hook - 增加額外延遲來避免狀態同步問題
  // 禁用自動頁面焦點恢復，由用戶手動控制
  const { triggerFocusRecovery } = usePageFocus({
    onFocus: handlePageFocusRecovery,
    debounceMs: 1000, // 增加到 1000ms，避免頁面切換時的狀態同步問題
    enabled: false
  });
  const [importStatus, setImportStatus] = useState<'idle'|'preview'|'success'|'error'>('idle');
  const [currentCompany, setCurrentCompany] = useState<{ id: string; code?: string; name?: string } | null>(null);


  useEffect(() => {
    (async () => {
      try {
        const company = await companyService.getCurrentCompany();
        if (company) setCurrentCompany({ id: String(company.id), code: company.code, name: company.name });
      } catch {}
    })();
  }, []);

  // 組件卸載時清理保存的狀態
  useEffect(() => {
    return () => {
      sessionStorage.removeItem(IMPORT_STATE_KEY);
      console.log('🗑️ SalesImport 組件卸載，清除保存的狀態');
    };
  }, []);

  // 移除不必要的軟重載 - SalesImportEngine 已經在監聽 isProcessing 狀態變化

  // 解析檔案只跑一次，結果快取在 rowsRef
  const parseOnce = async (file: File): Promise<PreviewRow[]> => {
    if (rowsRef.current) return rowsRef.current;
    
    const parsed = await parseFileToRows(file);
    rowsRef.current = parsed;
    setPreviewData(parsed);
    return parsed;
  };

  // 小刷新：只重掛載子元件
  const softRefresh = () => {
    console.log(`[softRefresh-${Date.now()}] 觸發組件重掛載`);
    setRevision(r => r + 1);
  };

  // 匯入按鈕點擊處理
  const handleImportClick = async (mode: 'reset' | 'resume' = 'reset') => {
    if (!selectedFile) {
      toast.error(language === 'zh' ? '請先選擇檔案' : 'Please select a file first');
      return;
    }

    if (errors.length > 0) {
      toast.error(language === 'zh' ? '檔案仍有錯誤，請修正後再匯入' : 'There are validation errors. Please fix them before importing');
      return;
    }

    console.log(`[handleImportClick] 開始處理，模式: ${mode}`);
    await parseOnce(selectedFile);
    // reset 從 0 開始；resume 從當前已完成的筆數開始（斷點續傳）
    setStartIndex(mode === 'reset' ? 0 : progress.done);

    // 設置處理狀態 - React 會在下一個渲染周期更新狀態
    console.log(`[handleImportClick] 設置 isProcessing = true`);
    setIsProcessing(true);
  };

  // 檔案選擇處理
  const handlePickFile = async (file: File) => {
    setSelectedFile(file);
    rowsRef.current = null; // 清除快取
    setStartIndex(0); // 新檔案從頭開始
    setErrors([]);
    setImportStatus('idle');
    setIsProcessing(false); // 重置處理狀態
    setProgress({ done: 0, total: 0 }); // 重置進度
    setFailedRows([]);
    
    try {
      await parseOnce(file);
      setImportStatus('preview');
    } catch (error) {
      setImportStatus('error');
      toast.error(language === 'zh' ? '檔案解析失敗' : 'File parsing failed');
    }
  };

  const toStringStrict = (v: any) => (v === null || v === undefined ? '' : String(v));
  const toNumberOrNull = (v: any) => {
    const t = typeof v === 'string' ? v.trim() : v;
    if (t === '' || t === null || t === undefined) return null;
    const n = Number(t);
    return Number.isFinite(n) ? n : null;
  };

  // Security: Sanitize text to prevent XSS and Excel Formula Injection
  const sanitizeText = (text: string): string => {
    if (!text || typeof text !== 'string') return text;

    let sanitized = text.trim();

    // HTML escape to prevent XSS while preserving content (e.g., "<此日期前最佳：2026.12>" becomes "&lt;此日期前最佳：2026.12&gt;")
    // Escape order matters: & must be escaped first to avoid double-escaping
    sanitized = sanitized
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');

    // Prevent Excel Formula Injection by prefixing dangerous formulas with '
    if (/^[=+\-@]/.test(sanitized)) {
      sanitized = "'" + sanitized;
    }

    // Additional security: Limit text length to prevent DoS
    if (sanitized.length > 5000) {
      sanitized = sanitized.substring(0, 5000) + '...';
    }

    return sanitized;
  };

  // Security: Validate numeric fields with range checks
  const validateNumericField = (value: any, fieldName: string, min: number = 0): { isValid: boolean; error?: string } => {
    const num = toNumberOrNull(value);
    if (num === null) return { isValid: true }; // Allow null values

    if (!Number.isFinite(num)) {
      return { isValid: false, error: language === 'zh' ? `${fieldName}必須是有效的數字` : `${fieldName} must be a valid number` };
    }

    if (num < min) {
      return { isValid: false, error: language === 'zh' ? `${fieldName}不能小於 ${min}` : `${fieldName} cannot be less than ${min}` };
    }

    // Additional check for extremely large numbers (potential DoS)
    if (num > 999999999) {
      return { isValid: false, error: language === 'zh' ? `${fieldName}數值過大，請檢查數據` : `${fieldName} value too large, please check data` };
    }

    return { isValid: true };
  };
  const toDateIsoOrNull = (v: any, time?: any) => {
    const s = (v ?? '').toString().trim();
    let dt: Date | null = null;
    if (!s) return null;
    if (/^\d{4}-\d{2}-\d{2}/.test(s)) dt = new Date(s);
    if (!dt && /\d{1,2}\/\d{1,2}\/\d{2,4}/.test(s)) dt = new Date(s);
    if (!dt) {
      // Try Excel date serial
      const n = Number(s);
      if (Number.isFinite(n)) {
        const excelEpoch = new Date(Date.UTC(1899, 11, 30));
        dt = new Date(excelEpoch.getTime() + n * 86400000);
      }
    }
    if (dt && time) {
      const t = String(time).trim();
      if (t && /^\d{1,2}:\d{2}(:\d{2})?$/.test(t)) {
        const [hh, mm, ss] = t.split(':').map((x) => Number(x));
        dt.setHours(hh || 0, mm || 0, ss || 0, 0);
      }
    }
    // 不使用 toISOString() 避免 UTC 轉換，直接返回本地時間的 ISO 字符串
    // 格式: YYYY-MM-DDTHH:mm:ss.sss (本地時間)
    if (dt) {
      const year = dt.getFullYear();
      const month = String(dt.getMonth() + 1).padStart(2, '0');
      const day = String(dt.getDate()).padStart(2, '0');
      const hours = String(dt.getHours()).padStart(2, '0');
      const minutes = String(dt.getMinutes()).padStart(2, '0');
      const seconds = String(dt.getSeconds()).padStart(2, '0');
      const milliseconds = String(dt.getMilliseconds()).padStart(3, '0');
      return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.${milliseconds}`;
    }
    return null;
  };

  // 嚴格要求三個必要欄位（只接受指定的正式名稱；不接受近似）
  // 用於偵測真正的表頭列（A1~A4 前言會被跳過）。
  // 需出現的最小集合：Order ID / SKU ID / Qty (Q)
  const requiredHeaders = ['order_id', 'sku_id', 'sale_quantity'];

  const downloadTemplate = () => {
    // 暂时禁用下载模板功能，之后会重新处理
    // TODO: 重新实现模板下载功能
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Security: File size limit to prevent DoS attacks
    const MAX_SIZE = 5 * 1024 * 1024; // 5MB limit
    if (file.size > MAX_SIZE) {
      toast.error(language === 'zh' ? '檔案太大！請上傳 5MB 以下的檔案。' : 'File too large! Please upload files under 5MB.');
      e.target.value = '';
      return;
    }

    const companyCode = (currentCompany?.code || '').toString().trim();
    if (!companyCode) {
      toast.error(language === 'zh' ? '未設定當前公司的「公司代碼」，無法驗證 Store ID。' : 'Company code is not set. Cannot validate Store ID.');
      e.target.value = '';
      return;
    }

    await handlePickFile(file);
  };

  // 重構：檔案解析邏輯，返回 PreviewRow[]
  const parseFileToRows = async (file: File): Promise<PreviewRow[]> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (ev) => {
        try {
          const parsed: PreviewRow[] = [];
          const validation: ImportError[] = [];
          const isExcel = file.name.toLowerCase().endsWith('.xlsx') || file.name.toLowerCase().endsWith('.xls');
          let headers: string[] = [];
          let rows: any[][] = [];
          let allRows: any[][] = [];

          // 預期的資料庫欄位順序（用於沒有標題行的 Excel）
          const expectedDbKeys: string[] = [
            'delivery_mode','warehouse_id','warehouse_name','order_id','sub_order_id','shipment_id',
            'order_date','order_time','pickup_date','pickup_time','delivery_date','delivery_time',
            'sale_sub_category_code','sale_sub_category_name','sale_mix_match_promo_id','mix_match_promo_name_en',
            'mix_match_promo_name_zh','sku_id','sale_sku_brand_en','sale_sku_brand_zh','sale_sku_name_en',
            'sale_sku_name_zh','sku_spec','sale_quantity','sale_unit_price','store_credit_discount',
            'total_amount','sale_status','recipient_name','contact_number','delivery_address',
            'require_removal_service','delivery_district','add_on_promotion'
          ];
          
          if (isExcel) {
            const wb = XLSX.read(new Uint8Array(ev.target?.result as ArrayBuffer), { type: 'array' });
            const sheet = wb.Sheets[wb.SheetNames[0]];
            allRows = XLSX.utils.sheet_to_json<any>(sheet, { header: 1, defval: '' }) as any[];
          } else {
            const text = ev.target?.result as string;
            allRows = text.split('\n').filter((r) => r.trim()).map((line) => line.split(',').map((v) => v.trim().replace(/"/g, '')));
          }
          
          if (allRows.length < 2) throw new Error('檔案至少需一行標題與一行資料');
          
          // 前言區掃描 Store ID
          const prefaceRows = allRows.slice(0, Math.min(10, allRows.length));
          let detectedStoreId: string | null = null;
          for (const r of prefaceRows) {
            const line = (r || []).map((v: any) => String(v)).join(' ');
            const m = /Store\s*ID\s*:?\s*([A-Za-z0-9_-]+)/i.exec(line);
            if (m && m[1]) { detectedStoreId = m[1].trim(); break; }
          }
          
          const expectedCompanyCode = (currentCompany?.code || '').toString().trim();
          if (!detectedStoreId) {
            throw new Error(language === 'zh' ? '前言未找到 Store ID，已阻止匯入' : 'Store ID not found in preface. Import blocked');
          }
          if (!expectedCompanyCode) {
            throw new Error(language === 'zh' ? '未設定公司代碼，已阻止匯入' : 'Company code not set. Import blocked');
          }
          if (detectedStoreId !== expectedCompanyCode) {
            throw new Error(language === 'zh'
              ? `Store ID (${detectedStoreId}) 與公司代碼 (${expectedCompanyCode}) 不一致`
              : `Store ID (${detectedStoreId}) does not match company code (${expectedCompanyCode})`);
          }
          
          const normalized = (s: string) => s
            ?.toString()
            .replace(/\r?\n/g, ' ')
            .replace(/"/g, '')
            .replace(/\s+/g, ' ')
            .trim()
            .toLowerCase();
            
          // 表頭映射
          const headerMap: Record<string, string> = {
            'delivery mode': 'delivery_mode',
            'warehouse id': 'warehouse_id',
            'warehouse name': 'warehouse_name',
            'order id': 'order_id',
            'sub-order id': 'sub_order_id',
            'sub order id': 'sub_order_id',
            'shipment id': 'shipment_id',
            'order date': 'order_date',
            'order time': 'order_time',
            'pickup date': 'pickup_date',
            'pickup time': 'pickup_time',
            'delivery date': 'delivery_date',
            'delivery time': 'delivery_time',
            'sub category code': 'sale_sub_category_code',
            'sub category name': 'sale_sub_category_name',
            'mix & match promo id': 'sale_mix_match_promo_id',
            'mix & match promo name (english)': 'mix_match_promo_name_en',
            'mix & match promo name (chinese)': 'mix_match_promo_name_zh',
            'sku id': 'sku_id',
            'sku brand (english)': 'sale_sku_brand_en',
            'sku brand (chinese)': 'sale_sku_brand_zh',
            'sku name (english)': 'sale_sku_name_en',
            'sku name (chinese)': 'sale_sku_name_zh',
            'sku spec': 'sku_spec',
            'qty (q)': 'sale_quantity',
            'unit price (u)': 'sale_unit_price',
            'store credit / discount applied (d)': 'store_credit_discount',
            'total q * u - d = t': 'total_amount',
            'status': 'sale_status',
            'recipient name': 'recipient_name',
            'contact number': 'contact_number',
            'delivery address': 'delivery_address',
            'require removal service': 'require_removal_service',
            'delivery district': 'delivery_district',
            'add-on promotion': 'add_on_promotion',
          };
          
          // 檢測表頭行
          let headerRowIdx = -1;
          for (let i = 0; i < Math.min(allRows.length, 20); i++) {
            const candidate = (allRows[i] || []).map((v: any) => String(v));
            const candMapped = candidate.map((h) => headerMap[normalized(h)] || null);
            const ok = requiredHeaders.every((col) => candMapped.includes(col));
            if (ok) {
              headerRowIdx = i;
              headers = candidate;
              rows = allRows.slice(i + 1);
              break;
            }
          }
          
          if (headerRowIdx === -1) {
            console.log('❌ 未找到標題行，將使用固定欄位順序解析');
            // 使用固定欄位順序，因為 Excel 沒有標題行
            headers = [];
            rows = allRows; // 所有行都是資料行

            // 使用預期的欄位順序來建立映射
            const mappedKeys: (string | null)[] = expectedDbKeys.map(key => key);
            console.log('📋 使用固定欄位順序:', expectedDbKeys.join(', '));

            // 解析所有資料行
            rows.forEach((cols, idx) => {
              const obj: PreviewRow = {};
              expectedDbKeys.forEach((dbKey, ci) => {
                if (dbKey && cols[ci] !== undefined) {
                  obj[dbKey] = cols[ci] ?? '';
                }
              });

            // Security: Apply data sanitization
            obj.order_id = sanitizeText(toStringStrict(obj.order_id));
            obj.sku_id = sanitizeText(toStringStrict(obj.sku_id));
            obj.recipient_name = sanitizeText(obj.recipient_name);
            obj.contact_number = sanitizeText(obj.contact_number);
            obj.delivery_address = sanitizeText(obj.delivery_address);

            // Security: Enhanced numeric validation with range checks
            const quantityValidation = validateNumericField(obj.sale_quantity, language === 'zh' ? '銷售數量' : 'Sale Quantity');
            if (!quantityValidation.isValid) {
              validation.push({ row: idx + 1, field: 'sale_quantity', message: quantityValidation.error! });
            }

            const priceValidation = validateNumericField(obj.sale_unit_price, language === 'zh' ? '單價' : 'Unit Price');
            if (!priceValidation.isValid) {
              validation.push({ row: idx + 1, field: 'sale_unit_price', message: priceValidation.error! });
            }

            // 驗證必要字段
            if (!obj.order_id) validation.push({ row: idx + 1, field: 'order_id', message: '缺少 Order ID' });
            if (!obj.sku_id) validation.push({ row: idx + 1, field: 'sku_id', message: '缺少 SKU ID' });
            if (!obj.sale_quantity) validation.push({ row: idx + 1, field: 'sale_quantity', message: '缺少 Qty (Q)' });
            if ((obj.sale_unit_price === null || obj.sale_unit_price === undefined || obj.sale_unit_price === '') && (obj.total_amount === null || obj.total_amount === undefined || obj.total_amount === '')) validation.push({ row: idx + 1, field: 'sale_unit_price', message: '缺少 單價 或 合計金額' });

            parsed.push(obj);
            });

            resolve(parsed);
            return;
          }

          const mappedKeys: (string | null)[] = headers.map((h) => headerMap[normalized(h)] || null);
          
          const missingDbKeys = expectedDbKeys.filter((col) => !mappedKeys.includes(col));
          if (missingDbKeys.length > 0) {
            const labelMap: Record<string, string> = {
              delivery_mode: 'Delivery Mode', warehouse_id: 'Warehouse ID', warehouse_name: 'Warehouse Name',
              order_id: 'Order ID', sub_order_id: 'Sub-Order ID', shipment_id: 'Shipment ID',
              order_date: 'Order Date', order_time: 'Order Time', pickup_date: 'Pickup Date',
              pickup_time: 'Pickup Time', delivery_date: 'Delivery Date', delivery_time: 'Delivery Time',
              sub_category_code: 'Sub category code', sub_category_name: 'Sub category name',
              sale_mix_match_promo_id: 'Mix & match Promo ID', mix_match_promo_name_en: 'Mix & match Promo name (English)',
              mix_match_promo_name_zh: 'Mix & match Promo name (Chinese)', sku_id: 'SKU ID',
              sale_sku_brand_en: 'SKU brand (English)', sale_sku_brand_zh: 'SKU brand (Chinese)',
              sale_sku_name_en: 'SKU Name (English)', sale_sku_name_zh: 'SKU Name (Chinese)',
              sku_spec: 'SKU Spec', sale_quantity: 'Qty (Q)', sale_unit_price: 'Unit Price (U)',
              store_credit_discount: 'Store Credit / Discount Applied (D)', total_amount: 'Total Q * U - D = T',
              sale_status: 'Status', recipient_name: 'Recipient Name', contact_number: 'Contact Number',
              delivery_address: 'Delivery Address', require_removal_service: 'Require Removal Service',
              delivery_district: 'Delivery District', add_on_promotion: 'Add-on Promotion',
            };
            const missingLabels = missingDbKeys.map((k) => labelMap[k] || k).join(', ');
            const msg = language === 'zh'
              ? `缺少必要欄位：${missingLabels}`
              : `Missing required columns: ${missingLabels}`;
            throw new Error(msg);
          }
          
          // 解析數據行
          rows.forEach((cols, idx) => {
            const obj: PreviewRow = {};
            mappedKeys.forEach((dbKey, ci) => { 
              if (dbKey) obj[dbKey] = cols[ci] ?? ''; 
            });
            
            // Security: Apply data sanitization
            obj.order_id = sanitizeText(toStringStrict(obj.order_id));
            obj.sku_id = sanitizeText(toStringStrict(obj.sku_id));
            obj.recipient_name = sanitizeText(obj.recipient_name);
            obj.contact_number = sanitizeText(obj.contact_number);
            obj.delivery_address = sanitizeText(obj.delivery_address);

            // Security: Enhanced numeric validation with range checks
            const quantityValidation = validateNumericField(obj.sale_quantity, language === 'zh' ? '銷售數量' : 'Sale Quantity');
            if (!quantityValidation.isValid) {
              validation.push({ row: idx + 3, field: 'sale_quantity', message: quantityValidation.error! });
            }

            const priceValidation = validateNumericField(obj.sale_unit_price, language === 'zh' ? '單價' : 'Unit Price');
            if (!priceValidation.isValid) {
              validation.push({ row: idx + 3, field: 'sale_unit_price', message: priceValidation.error! });
            }

            // 驗證必要字段
            if (!obj.order_id) validation.push({ row: idx + 3, field: 'order_id', message: '缺少 Order ID' });
            if (!obj.sku_id) validation.push({ row: idx + 3, field: 'sku_id', message: '缺少 SKU ID' });
            if (!obj.sale_quantity) validation.push({ row: idx + 3, field: 'sale_quantity', message: '缺少 Qty (Q)' });
            if ((obj.sale_unit_price === null || obj.sale_unit_price === undefined || obj.sale_unit_price === '') && (obj.total_amount === null || obj.total_amount === undefined || obj.total_amount === '')) validation.push({ row: idx + 3, field: 'sale_unit_price', message: '缺少 單價 或 合計金額' });

            parsed.push(obj);
          });
          
          setErrors(validation);
          
          if (validation.length > 0) {
            toast.error(language === 'zh' ? `檔案驗證失敗，共 ${validation.length} 筆錯誤。請修正後再匯入。` : `File validation failed with ${validation.length} errors. Please fix and try again.`);
            reject(new Error(`Validation failed with ${validation.length} errors`));
          } else {
            toast.success(language === 'zh' ? 'Excel 已準備，可隨時按下「確定滙入」' : 'Excel is ready. You can click "Confirm Import" anytime');
            resolve(parsed);
          }
        } catch (err: any) {
          toast.error('檔案解析失敗: ' + err.message);
          reject(err);
        }
      };
      
      reader.onerror = () => {
        const error = new Error('Failed to read file');
        toast.error(language === 'zh' ? '讀取檔案失敗' : 'Failed to read file');
        reject(error);
      };
      
      if (file.name.toLowerCase().endsWith('.xlsx') || file.name.toLowerCase().endsWith('.xls')) {
        reader.readAsArrayBuffer(file);
      } else {
        reader.readAsText(file);
      }
    });
  };

  const resetImport = () => {
    setSelectedFile(null);
    setPreviewData([]);
    setErrors([]);
    setImportStatus('idle');
    setIsProcessing(false); // 重置處理狀態

    // 清除保存的匯入狀態
    sessionStorage.removeItem(IMPORT_STATE_KEY);
    console.log('🗑️ 重置匯入，清除保存的狀態');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // 進度回報處理（節流）
  const handleProgress = useCallback((index: number, total?: number) => {
    const finalTotal = total || rowsRef.current?.length || previewData.length || 0;
    // 只更新進度顯示，不更新 startIndex（避免重渲染）
    setProgress({ done: Math.min(index, finalTotal), total: finalTotal });

    // 更新活動時間戳，用於頁面焦點恢復檢測
    lastActivityRef.current = Date.now();
  }, []);

  // 使用 ref 鏡像 failedRows，讓 handleComplete 不依賴 failedRows state（避免 useCallback deps 不穩定）
  const failedRowsRef = useRef<{ excelRow: number; message: string }[]>([]);

  // 導入完成處理（僅在全部處理結束時顯示一次總結）
  const handleComplete = useCallback(() => {
    setIsProcessing(false);
    setImportStatus('success');

    // 清除保存的匯入狀態
    sessionStorage.removeItem(IMPORT_STATE_KEY);
    console.log('🗑️ 匯入完成，清除保存的狀態');

    const totalRows = rowsRef.current?.length || 0;
    const failed = failedRowsRef.current.length;
    const success = Math.max(totalRows - failed, 0);

    // 完成時重置為總行數（表示已處理完所有資料）
    setStartIndex(totalRows);
    setProgress({ done: totalRows, total: totalRows });

    // 最終一次性通知
    toast.success(
      language === 'zh'
        ? `匯入完成！成功：${success} 筆，失敗：${failed} 筆。`
        : `Import completed! Success: ${success}, Failed: ${failed}.`
    );
  }, [language]);

  const handleResult = useCallback((info: { hardFailedRows: { excelRow: number; message: string }[] }) => {
    const rows = info.hardFailedRows || [];
    failedRowsRef.current = rows;
    setFailedRows(rows);
  }, []);

  const handleError = useCallback((info: { message: string; failedBatch?: { type: 'update' | 'insert'; batchIndex: number; size: number } }) => {
    setIsProcessing(false);
    setImportStatus('error');
    // 不清除 IMPORT_STATE_KEY，保留進度以便之後「續傳」
    toast.error(
      language === 'zh'
        ? '部分資料匯入失敗，請查看下方列表'
        : 'Some rows failed to import. Please check the error list below.'
    );
  }, [language]);

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center gap-4 mb-6">
          <Button
            variant="outline"
            onClick={() => {
              localStorage.setItem('selectedCategory', ORDER_CATEGORY);
              navigate(ROUTES.dashboard.root);
            }}
            className="bg-card/50 border-border text-muted-foreground hover:bg-accent/50"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {language === 'zh' ? '返回主控台' : 'Back to Dashboard'}
          </Button>
          <div className="flex items-center gap-2">
            <Upload className="w-6 h-6 text-purple-400" />
            <h1 className="text-2xl font-bold bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">{language === 'zh' ? '滙入功能（銷售記錄）' : 'Import Sales Records'}</h1>
          </div>
        </div>

        <Card className="bg-card/80 border-border mb-6">
          <CardHeader>
            <CardTitle className="text-foreground">{language === 'zh' ? '檔案上傳' : 'File Upload'}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg border border-border">
                <div className="flex items-center gap-3">
                  <FileText className="w-5 h-5 text-purple-400" />
                  <div>
                    <p className="text-foreground font-medium">{language === 'zh' ? '下載匯入模板' : 'Download Template'}</p>
                    <p className="text-muted-foreground text-sm">{language === 'zh' ? '使用HKTVmall 系統內的「訂單報表」匯出每日銷售單單據的 Excel 檔' : 'Use Excel file exported from HKTVmall system\'s "Order Reports" for daily sales records'}</p>
                  </div>
                </div>
                <Button onClick={downloadTemplate} variant="outline" className="bg-card/50 border-border text-muted-foreground hover:bg-secondary/50">
                  <Download className="w-4 h-4 mr-2" />
                  {language === 'zh' ? '下載模板' : 'Download'}
                </Button>
              </div>

              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <input ref={fileInputRef} type="file" accept=".csv,.xlsx,.xls" onChange={handleFileSelect} className="hidden" id="sales-import-file" title={language === 'zh' ? '選擇匯入檔案（CSV 或 Excel）' : 'Choose import file (CSV or Excel)'} />
                  <Button onClick={() => fileInputRef.current?.click()} className="bg-purple-600 hover:bg-purple-700 text-white" disabled={isProcessing}>
                    <Upload className="w-4 h-4 mr-2" />
                    {language === 'zh' ? '選擇檔案' : 'Choose File'}
                  </Button>
                  {selectedFile && (
                    <div className="flex items-center gap-2">
                      <span className="text-foreground">{selectedFile.name}</span>
                      <Badge variant="outline" className="text-muted-foreground border-border">{(selectedFile.size / 1024).toFixed(1)} KB</Badge>
                      <Button variant="ghost" size="sm" onClick={resetImport} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></Button>
                    </div>
                  )}
                </div>
                {isProcessing && (
                  <div className="flex items-center gap-3 text-muted-foreground">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-400"></div>
                    <span>
                      {language === 'zh'
                        ? `正在處理第 ${progress.done}/${progress.total} 筆...`
                        : `Processing ${progress.done}/${progress.total} rows...`}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {previewData.length > 0 && (
          <Card className="bg-card/80 border-border">
            <CardHeader>
              <CardTitle className="text-foreground">{language === 'zh' ? '資料預覽 (前 10 筆)' : 'Preview (first 10 rows)'}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-border">
                      <TableHead className="text-muted-foreground">{language === 'zh' ? '訂單編號' : 'Order ID'}</TableHead>
                      <TableHead className="text-muted-foreground">{language === 'zh' ? '倉庫代碼' : 'Warehouse ID'}</TableHead>
                      <TableHead className="text-muted-foreground">{language === 'zh' ? '品牌' : 'Brand'}</TableHead>
                      <TableHead className="text-muted-foreground">{language === 'zh' ? '產品名稱' : 'Product Name'}</TableHead>
                      <TableHead className="text-muted-foreground">{language === 'zh' ? '數量' : 'Quantity'}</TableHead>
                      <TableHead className="text-muted-foreground">{language === 'zh' ? '價格' : 'Price'}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {previewData.slice(0, 10).map((row, index) => {
                      const brand = language === 'zh' ? (row.sale_sku_brand_zh ?? '') : (row.sale_sku_brand_en ?? '');
                      const name = language === 'zh' ? (row.sale_sku_name_zh ?? '') : (row.sale_sku_name_en ?? '');
                      return (
                        <TableRow key={index} className="border-border hover:bg-accent/50">
                          <TableCell className="text-foreground text-sm">{String(row.order_id || '')}</TableCell>
                          <TableCell className="text-foreground text-sm">{String(row.warehouse_id || '')}</TableCell>
                          <TableCell className="text-foreground text-sm">{decodeHtmlEntities(String(brand))}</TableCell>
                          <TableCell className="text-foreground text-sm">{decodeHtmlEntities(String(name))}</TableCell>
                          <TableCell className="text-foreground text-sm">{String(row.sale_quantity ?? '')}</TableCell>
                          <TableCell className="text-foreground text-sm">{String(row.sale_unit_price ?? '')}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
              <div className="mt-4 flex flex-wrap gap-3">
                <Button 
                  type="button"
                  onClick={() => handleImportClick('reset')} 
                  className="bg-green-600 hover:bg-green-700 text-white"
                  disabled={isProcessing || errors.length > 0}
                >
                  {language === 'zh' ? '確定滙入' : 'Confirm Import'}
                </Button>
                {importStatus === 'error' && progress.done < progress.total && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => handleImportClick('resume')}
                    disabled={isProcessing || errors.length > 0}
                    className="border-yellow-500 text-yellow-500 hover:bg-yellow-500/10"
                  >
                    {language === 'zh'
                      ? `從第 ${progress.done + 1} 筆繼續匯入`
                      : `Resume from row ${progress.done + 1}`}
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {failedRows.length > 0 && (
          <Card className="bg-red-50/10 border border-red-500 mt-4">
            <CardHeader>
              <CardTitle className="text-foreground">
                <span className="inline-flex items-center gap-2">
                  <span className="text-red-500 font-bold">⚠️</span>
                  <span>
                    {language === 'zh' ? '匯入失敗行列表' : 'Failed Import Rows'}
                  </span>
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mb-2">
                {language === 'zh'
                  ? '以下為經過多次重試後仍無法匯入的 Excel 原始行號與原因：'
                  : 'These Excel rows still failed to import after multiple retries, with reasons:'}
              </p>
              <div className="max-h-60 overflow-auto border border-border rounded-md">
                <Table>
                  <TableHeader>
                    <TableRow className="border-border">
                      <TableHead className="text-muted-foreground">
                        {language === 'zh' ? 'Excel 行號' : 'Excel Row'}
                      </TableHead>
                      <TableHead className="text-muted-foreground">
                        {language === 'zh' ? '錯誤原因（摘要）' : 'Error (summary)'}
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {failedRows.map((r, idx) => (
                      <TableRow key={`${r.excelRow}-${idx}`} className="border-border">
                        <TableCell className="text-foreground">{r.excelRow}</TableCell>
                        <TableCell className="text-muted-foreground text-xs">
                          {r.message}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* 導入引擎子元件 */}
        <SalesImportEngine
          key={revision}
          file={selectedFile}
          rows={rowsRef.current}
          startIndex={startIndex}
          onProgress={handleProgress}
          onComplete={handleComplete}
          onError={handleError}
          onResult={handleResult}
          isProcessing={isProcessing}
          currentCompany={currentCompany}
          isPageFocusRecovery={isPageFocusRecovery}
        />
      </div>
    </div>
  );
};

// 子元件：銷售導入引擎
const SalesImportEngine: React.FC<SalesImportEngineProps> = ({
  file,
  rows,
  startIndex = 0,
  onProgress,
  onComplete,
  onError,
  onResult,
  isProcessing = false,
  currentCompany,
  isPageFocusRecovery = false
}) => {
  const { language } = useLanguage();
  const abortRef = useRef<AbortController | null>(null);

  // 調試：記錄接收到的 props
  console.log(`[SalesImportEngine-${Date.now()}] Props: file=${!!file}, rows=${rows?.length}, company=${!!currentCompany?.id}, startIndex=${startIndex}, isProcessing=${isProcessing}`);

  // 添加組件實例標記，用於防止組件重掛載時的競爭條件
  const instanceId = useRef(Math.random().toString(36).substr(2, 9));
  const isMountedRef = useRef(true);

  useEffect(() => {
    console.log(`[SalesImportEngine-${instanceId.current}-${Date.now()}] useEffect 執行開始`);

    // 重置掛載狀態
    isMountedRef.current = true;

    console.log(`[SalesImportEngine-${instanceId.current}-${Date.now()}] 條件檢查:`);
    console.log(`  - file: ${!!file} (${typeof file})`);
    console.log(`  - rows: ${rows?.length} (${typeof rows})`);
    console.log(`  - rows.length: ${rows?.length}`);
    console.log(`  - company: ${!!currentCompany?.id} (${currentCompany?.id})`);
    console.log(`  - startIndex: ${startIndex}`);
    console.log(`  - startIndex >= rows.length: ${startIndex >= (rows?.length || 0)}`);
    console.log(`  - isProcessing: ${isProcessing} (${typeof isProcessing})`);

    const condition1 = !file;
    const condition2 = !rows;
    const condition3 = rows && rows.length === 0;
    const condition4 = !currentCompany?.id;
    const condition5 = startIndex >= (rows?.length || 0);
    const condition6 = !isProcessing;

    console.log(`[SalesImportEngine-${instanceId.current}-${Date.now()}] 條件詳情:`);
    console.log(`  - !file: ${condition1}`);
    console.log(`  - !rows: ${condition2}`);
    console.log(`  - rows.length === 0: ${condition3}`);
    console.log(`  - !currentCompany?.id: ${condition4}`);
    console.log(`  - startIndex >= rows.length: ${condition5}`);
    console.log(`  - !isProcessing: ${condition6}`);

    const shouldSkip = condition1 || condition2 || condition3 || condition4 || condition5 || condition6;

    if (shouldSkip) {
      console.log(`[SalesImportEngine-${instanceId.current}-${Date.now()}] ❌ 跳過處理`);
      return;
    }

    console.log(`[SalesImportEngine-${instanceId.current}-${Date.now()}] ✅ 條件滿足，開始處理！`);

    // 🔒 頁面焦點恢復保護：如果正在頁面焦點恢復期間，跳過處理
    if (isPageFocusRecovery) {
      console.log(`[SalesImportEngine-${instanceId.current}-${Date.now()}] 🔒 頁面焦點恢復期間，跳過處理以避免競爭條件`);
      return;
    }

    // 設置總數量
    onProgress(startIndex, rows.length);

    let lastTick = 0;

    const run = async () => {
      console.log(`[SalesImportEngine-${instanceId.current}-${Date.now()}] 🚀 run函數開始執行`);
       // 檢查組件是否還掛載著（防止組件重掛載時的競爭條件）
      if (!isMountedRef.current) {
        console.log(`[SalesImportEngine-${instanceId.current}-${Date.now()}] 組件已卸載，跳過處理`);
        return;
      }

      // 中止任何舊工作
      abortRef.current?.abort();
      const ctl = new AbortController();
      abortRef.current = ctl;

      const CHUNK = 50; // 每批處理50筆

      try {
        // 輔助函數
        const toStringStrict = (v: any) => (v === null || v === undefined ? '' : String(v));
        const toNumberOrNull = (v: any) => {
          const t = typeof v === 'string' ? v.trim() : v;
          if (t === '' || t === null || t === undefined) return null;
          const n = Number(t);
          return Number.isFinite(n) ? n : null;
        };
        const toDateIsoOrNull = (v: any, time?: any) => {
          const s = (v ?? '').toString().trim();
          let dt: Date | null = null;
          if (!s) return null;
          if (/^\d{4}-\d{2}-\d{2}/.test(s)) dt = new Date(s);
          if (!dt && /\d{1,2}\/\d{1,2}\/\d{2,4}/.test(s)) dt = new Date(s);
          if (!dt) {
            const n = Number(s);
            if (Number.isFinite(n)) {
              const excelEpoch = new Date(Date.UTC(1899, 11, 30));
              dt = new Date(excelEpoch.getTime() + n * 86400000);
            }
          }
          if (dt && time) {
            const t = String(time).trim();
            if (t && /^\d{1,2}:\d{2}(:\d{2})?$/.test(t)) {
              const [hh, mm, ss] = t.split(':').map((x) => Number(x));
              dt.setHours(hh || 0, mm || 0, ss || 0, 0);
            }
          }
          return dt ? dt.toISOString() : null;
        };

        const { supabase } = await import('@/integrations/supabase/client');
        const companyId = currentCompany.id; // 使用 UUID 類型，不轉換為 number

        // 第一步：批量收集所有 shipment_id + sku_id 組合並查詢現有記錄
        // 第一步：一次性查詢所有現有記錄（只執行一次）
        const normalizeShipmentId = (id: any): string | null => {
          if (!id) return null;
          const normalized = String(id).trim().toUpperCase();
          return normalized.length > 0 ? normalized : null;
        };

        // 收集所有唯一的 shipment_id + sku_id 組合
        const uniqueCombinations = new Set<string>();
        rows.forEach(row => {
          const shipmentId = normalizeShipmentId(row.shipment_id);
          const skuId = String(row.sku_id || '').trim();
          if (shipmentId && skuId) {
            uniqueCombinations.add(`${shipmentId}:${skuId}`);
          }
        });

        const existingRecordsMap = new Map<string, number>(); // shipment_id:sku_id -> record_id

        if (uniqueCombinations.size > 0) {
          console.log(`🚀 [V2-一次性查詢-${Date.now()}] Checking for existing records with ${uniqueCombinations.size} unique shipment_id:sku_id combinations...`);

          // 將 Set 轉換為陣列以便查詢
          const combinationsArray = Array.from(uniqueCombinations);

          // 📅 第一步：從匯入資料中提取年月範圍（使用 order_date 欄位）
          const yearMonths = new Set<string>();
          rows.forEach(row => {
            if (row.order_date) {
              try {
                const date = new Date(row.order_date);
                if (!isNaN(date.getTime())) {
                  const year = date.getFullYear();
                  const month = date.getMonth() + 1; // 1-12
                  // 使用 YYYY-MM 格式來匹配 order_date 的前7個字符
                  yearMonths.add(`${year}-${month.toString().padStart(2, '0')}`);
                }
              } catch (e) {
                // 忽略無效日期
              }
            }
          });

          console.log(`📅 [時間範圍優化] 匯入資料包含 ${yearMonths.size} 個年月: ${Array.from(yearMonths).join(', ')}`);

          // 📅 第二步：使用 order_date 欄位建構查詢條件
          let queryBuilder = supabase
            .from('sales_orders')
            .select('id, shipment_id, sku_id')
            .eq('company_id', companyId);

          if (yearMonths.size > 0) {
            // 建構日期範圍條件，更精確且高效
            const dateRangeConditions: string[] = [];
            Array.from(yearMonths).forEach(ym => {
              const [year, month] = ym.split('-');
              const monthNum = parseInt(month);
              const startDate = `${year}-${month.padStart(2, '0')}-01`;

              // 計算下個月第一天
              const nextMonth = monthNum === 12 ? 1 : monthNum + 1;
              const nextYear = monthNum === 12 ? parseInt(year) + 1 : parseInt(year);
              const endDate = `${nextYear}-${nextMonth.toString().padStart(2, '0')}-01`;

              dateRangeConditions.push(`and(order_date.gte.${startDate},order_date.lt.${endDate})`);
            });

            queryBuilder = queryBuilder.or(dateRangeConditions.join(','));
            console.log(`🔍 [查詢優化] 使用日期範圍查詢 ${yearMonths.size} 個月，精確限制時間範圍`);
          } else {
            console.log(`⚠️ [查詢備用] 未找到有效日期範圍，使用全公司查詢（可能受 1000 行限制影響）`);
          }

          // 查詢匹配的 shipment_id + sku_id 記錄
          const { data: existingRecords, error: queryErr } = await queryBuilder;

          if (queryErr) {
            throw new Error(`Failed to query existing records: ${queryErr.message}`);
          }

          // 建立 shipment_id:sku_id -> record_id 的映射
          existingRecords?.forEach(record => {
            if (record.shipment_id && record.sku_id) {
              const normalizedShipmentId = normalizeShipmentId(record.shipment_id);
              const normalizedSkuId = String(record.sku_id).trim();
              if (normalizedShipmentId && normalizedSkuId) {
                const combinationKey = `${normalizedShipmentId}:${normalizedSkuId}`;
                existingRecordsMap.set(combinationKey, record.id);
                console.log(`[建立映射] ${combinationKey} -> ${record.id}`);
              }
            } else {
              console.log(`[跳過記錄] shipment_id: ${record.shipment_id}, sku_id: ${record.sku_id}`);
            }
          });

          console.log(`[一次性查詢] Found ${existingRecordsMap.size} existing records to update from ${existingRecords?.length || 0} scoped records`);

          // 調試：顯示所有組合鍵
          const allKeys = Array.from(existingRecordsMap.keys());
          console.log(`[所有組合鍵] ${allKeys.join(', ')}`);

          // 特別檢查我們的測試案例
          const testKey1 = '7429620572:3182550711159';
          const testKey2 = '7429620572:9003579010228';
          console.log(`[測試檢查] ${testKey1} exists: ${existingRecordsMap.has(testKey1)}`);
          console.log(`[測試檢查] ${testKey2} exists: ${existingRecordsMap.has(testKey2)}`);
        }

        // 第二步：分組處理 - 準備更新和新增的批次
        const recordsToUpdate: { id: number; data: any }[] = [];
        const recordsToInsert: any[] = [];
        // 供二分遞迴用的寫入條目，帶有 Excel 行號
        type WriteEntry = { mode: 'update' | 'insert'; payload: any; excelRow: number };
        const updateEntries: WriteEntry[] = [];
        const insertEntries: WriteEntry[] = [];
        let permissionToastShown = false;

        // 批量處理邏輯
        const totalRows = rows.length;
        console.log(`[批量處理-${Date.now()}] 開始處理 ${totalRows} 筆資料`);

        // ============================================================================
        // 🚀 SaaS 標準批量查詢優化 - 避免 N+1 查詢問題
        // ============================================================================

        // 第 1 階段：預處理 - 收集所有需要查詢的 SKU
        console.log(`[階段 1] 預處理：收集 SKU 資訊...`);
        onProgress(5, totalRows); // 进度更新：5%
        const uniqueSkus = new Set<string>();
        const processedRows: Array<{
          index: number;
          excelRow: number;
          row: any;
          payload: Record<string, any>;
        }> = [];

        // 一次性處理所有行，準備數據結構；支援從 startIndex 斷點續傳
        for (let i = 0; i < totalRows; i++) {
          if (ctl.signal.aborted || !isMountedRef.current) break;

          // 已處理過的行（小於 startIndex）僅用於查詢輔助，不重複寫入
          const isBeforeStart = i < startIndex;

          const row = rows[i];
          const skuId = toStringStrict(row.sku_id);
          if (skuId) {
            uniqueSkus.add(skuId);
          }

          // 準備基本數據
          const orderId = toStringStrict(row.order_id);
          const lineNo = Number(row.line_no || 1) || 1;
          const quantity = toNumberOrNull(row.sale_quantity) ?? 0;
          const unitPrice = toNumberOrNull(row.sale_unit_price) ?? 0;
          const orderDatetime = row.order_datetime ? toDateIsoOrNull(row.order_datetime) : toDateIsoOrNull(row.order_date, row.order_time);
          const status = row.sale_status ? String(row.sale_status) : null;
          const pickupDatetime = toDateIsoOrNull(row.pickup_date, row.pickup_time);
          const deliveryDatetime = toDateIsoOrNull(row.delivery_date, row.delivery_time);
          const storeCredit = toNumberOrNull(row.store_credit_discount);
          const totalAmount = row.total_amount != null ? (toNumberOrNull(row.total_amount) ?? null) : null;

          // 提取原始日期和時間字段（保持Excel原始格式）
          const orderDate = row.order_date ? String(row.order_date).trim() : null;
          const orderTime = row.order_time ? String(row.order_time).trim() : null;
          const pickupDate = row.pickup_date ? String(row.pickup_date).trim() : null;
          const pickupTime = row.pickup_time ? String(row.pickup_time).trim() : null;
          const deliveryDate = row.delivery_date ? String(row.delivery_date).trim() : null;
          const deliveryTime = row.delivery_time ? String(row.delivery_time).trim() : null;

          const payload: Record<string, any> = {
            company_id: companyId,
            order_id: orderId,
            line_no: lineNo,
            sku_id: skuId,
            sale_quantity: quantity,
            sale_unit_price: unitPrice,
            total_amount: totalAmount ?? (quantity * unitPrice),
            order_datetime: orderDatetime,
            order_date: orderDate,
            order_time: orderTime,
            sale_status: status,
            pickup_datetime: pickupDatetime,
            pickup_date: pickupDate,
            pickup_time: pickupTime,
            delivery_datetime: deliveryDatetime,
            delivery_date: deliveryDate,
            delivery_time: deliveryTime,
            store_credit_discount: storeCredit,
            sale_delivery_mode: row.delivery_mode ? String(row.delivery_mode) : null,
            sale_warehouse_id: row.warehouse_id ? String(row.warehouse_id) : null,
            sale_warehouse_name: row.warehouse_name ? String(row.warehouse_name) : null,
            sub_order_id: row.sub_order_id ? String(row.sub_order_id) : null,
            shipment_id: row.shipment_id ? String(row.shipment_id) : null,
            sale_sub_category_code: row.sale_sub_category_code ? String(row.sale_sub_category_code) : null,
            sale_sub_category_name: row.sale_sub_category_name ? String(row.sale_sub_category_name) : null,
            sale_mix_match_promo_id: row.sale_mix_match_promo_id ? String(row.sale_mix_match_promo_id) : null,
            mix_match_promo_name_en: row.mix_match_promo_name_en ? String(row.mix_match_promo_name_en) : null,
            mix_match_promo_name_zh: row.mix_match_promo_name_zh ? String(row.mix_match_promo_name_zh) : null,
            sale_sku_brand_en: row.sale_sku_brand_en ? String(row.sale_sku_brand_en) : null,
            sale_sku_brand_zh: row.sale_sku_brand_zh ? String(row.sale_sku_brand_zh) : null,
            sale_sku_name_en: row.sale_sku_name_en ? String(row.sale_sku_name_en) : null,
            sale_sku_name_zh: row.sale_sku_name_zh ? String(row.sale_sku_name_zh) : null,
            sku_spec: row.sku_spec ? String(row.sku_spec) : null,
            recipient_name: row.recipient_name ? String(row.recipient_name) : null,
            contact_number: row.contact_number ? String(row.contact_number) : null,
            delivery_address: row.delivery_address ? String(row.delivery_address) : null,
            require_removal_service: row.require_removal_service ? String(row.require_removal_service) : null,
            delivery_district: row.delivery_district ? String(row.delivery_district) : null,
            add_on_promotion: row.add_on_promotion ? String(row.add_on_promotion) : null,
            channel: 'HKTVmall',
            // 生成 company_sku_key = company_id + '-' + sku_id
            ...(companyId && skuId && { company_sku_key: `${companyId}-${skuId}` })
          };

          const excelRow = i + 2; // 假設第 1 行為表頭

          // 僅從 startIndex 之後的行加入寫入隊列；進度條仍顯示整體第 i+1 筆
          if (!isBeforeStart) {
            processedRows.push({ index: i, excelRow, row, payload });
          }
          onProgress(i + 1, totalRows);
        }

        const skuArray = Array.from(uniqueSkus);
        console.log(`[階段 1] 完成：收集到 ${skuArray.length} 個唯一 SKU，處理了 ${processedRows.length} 行數據`);
        onProgress(10, totalRows); // 进度更新：10%

        // 第 2 階段：批量查詢成本和佣金數據
        console.log(`[階段 2] 批量查詢：獲取 ${skuArray.length} 個 SKU 的成本和佣金數據...`);
        onProgress(15, totalRows); // 进度更新：15%

        const costMap = new Map<string, number>();
        const commissionMap = new Map<string, number>();

        if (skuArray.length > 0) {
          try {
            // 回歸舊方法：直接用 sku_id + company_id 查詢成本
            console.log(`[階段 2] 直接查詢成本數據 (使用舊方法)...`);
            console.log(`[階段 2] 查詢條件 - company_id: ${companyId}, sku_count: ${skuArray.length}`);
            console.log(`[階段 2] SKU 列表:`, skuArray.slice(0, 5), skuArray.length > 5 ? `...還有 ${skuArray.length - 5} 個` : '');

            // 添加超時控制
            const queryPromise = supabase
              .from('product_costs')
              .select('sku_id, average_cost')
              .eq('company_id', companyId)
              .in('sku_id', skuArray)
              .order('average_cost_date', { ascending: false });

            const timeoutPromise = new Promise((_, reject) => {
              setTimeout(() => reject(new Error('查詢超時 (30秒)')), 30000);
            });

            let costData, costErr;
            try {
              const result = await Promise.race([queryPromise, timeoutPromise]);
              costData = result.data;
              costErr = result.error;
            } catch (err) {
              costData = null;
              costErr = err;
              console.error(`[階段 2] 查詢超時或其他錯誤:`, err);
            }

            console.log(`[階段 2] 查詢完成 - 錯誤: ${!!costErr}, 數據長度: ${costData?.length || 0}`);

            if (costErr) {
              console.error(`[階段 2] 查詢錯誤:`, costErr);
              console.error(`[階段 2] 錯誤詳情:`, {
                message: costErr.message,
                details: costErr.details,
                hint: costErr.hint,
                code: costErr.code
              });
            }

            if (!costErr && costData) {
              // 為每個 SKU 保留最新的成本記錄
              const costBySku = new Map<string, number>();
              costData.forEach(record => {
                if (!costBySku.has(record.sku_id)) {
                  costBySku.set(record.sku_id, record.average_cost);
                }
              });

              // 設置到全局映射
              costBySku.forEach((cost, skuId) => {
                costMap.set(skuId, cost);
              });

              console.log(`[階段 2] 成功查詢 ${costMap.size} 個 SKU 的成本數據 (舊方法)`);
              console.log(`[成本數據] 示例:`, Array.from(costMap.entries()).slice(0, 3));
            } else {
              console.error(`[階段 2] 成本查詢失敗:`, costErr);
            }

            onProgress(25, totalRows); // 进度更新：25%
          } catch (e) {
            console.warn('批量查詢成本失敗:', e);
          }

          try {
            // 第三步：查詢佣金數據
            console.log(`[階段 2.3] 查詢佣金數據...`);
            console.log(`[階段 2.3] 查詢條件 - company_id: ${companyId}, sku_count: ${skuArray.length}`);

            const { data: productData, error: productErr } = await supabase
              .from('products')
              .select('sku_id, category_code')
              .eq('company_id', companyId)
              .in('sku_id', skuArray);

            console.log(`[階段 2.3] Products查詢完成 - 錯誤: ${!!productErr}, 數據長度: ${productData?.length || 0}`);

            if (productErr) {
              console.error(`[階段 2.3] Products查詢錯誤:`, productErr);
              console.error(`[階段 2.3] Products錯誤詳情:`, {
                message: productErr.message,
                details: productErr.details,
                hint: productErr.hint,
                code: productErr.code
              });
            }

            if (!productErr && productData && productData.length > 0) {
              // 提取所有唯一的category_code
              const categoryCodes = [...new Set(productData.map(p => p.category_code).filter(Boolean))];
              console.log(`[階段 2.3] 找到 ${categoryCodes.length} 個唯一的分類代碼:`, categoryCodes);
              console.log(`[階段 2.3] Products數據示例:`, productData.slice(0, 3));

              if (categoryCodes.length > 0) {
                console.log(`[階段 2.3] 開始查詢categories表...`);
                const { data: categoryData, error: categoryErr } = await supabase
                  .from('categories')
                  .select('category_code, standard_delivery_commission')
                  .in('category_code', categoryCodes);

                console.log(`[階段 2.3] Categories查詢完成 - 錯誤: ${!!categoryErr}, 數據長度: ${categoryData?.length || 0}`);

                if (categoryErr) {
                  console.error(`[階段 2.3] Categories查詢錯誤:`, categoryErr);
                  console.error(`[階段 2.3] Categories錯誤詳情:`, {
                    message: categoryErr.message,
                    details: categoryErr.details,
                    hint: categoryErr.hint,
                    code: categoryErr.code
                  });
                }

                if (!categoryErr && categoryData) {
                  const categoryMap = new Map<string, number>();
                  categoryData.forEach(cat => {
                    if (cat.category_code && cat.standard_delivery_commission !== null) {
                      categoryMap.set(cat.category_code, cat.standard_delivery_commission);
                    }
                  });

                  console.log(`[階段 2.3] 成功查詢 ${categoryMap.size} 個分類的佣金數據`);
                  console.log(`[分類佣金] 示例:`, Array.from(categoryMap.entries()).slice(0, 3));

                  // 為每個 SKU 設置佣金率
                  productData.forEach(product => {
                    if (product.sku_id && product.category_code && categoryMap.has(product.category_code)) {
                      const commissionRate = categoryMap.get(product.category_code)!;
                      commissionMap.set(product.sku_id, commissionRate);
                    }
                  });

                  console.log(`[階段 2.3] 成功為 ${commissionMap.size} 個 SKU 設置佣金率`);
                  console.log(`[SKU佣金] 示例:`, Array.from(commissionMap.entries()).slice(0, 3));
                } else {
                  console.error(`[階段 2.3] 查詢 categories 表失敗:`, categoryErr);
                }
              } else {
                console.warn(`[階段 2.3] 沒有找到有效的分類代碼`);
              }
            } else {
              console.warn(`[階段 2.3] 重新查詢 products 表失敗或無數據:`, productErr);
            }

            onProgress(35, totalRows); // 进度更新：35%
          } catch (e) {
            console.warn('批量查詢佣金失敗:', e);
          }
        }

        // 第 3 階段：在內存中匹配和計算
        console.log(`[階段 3] 內存計算：為 ${processedRows.length} 行數據匹配成本和計算佣金...`);
        onProgress(40, totalRows); // 进度更新：40%

        // 一次性查詢所有可能的現有記錄 - 改為更精確的查詢方式
        console.log(`[階段 3] 開始查詢現有記錄...`);

        // 收集所有需要檢查的 shipment_id:sku_id 組合
        const combinationsToCheck = new Set<string>();
        processedRows.forEach(({ row }) => {
          const shipmentId = normalizeShipmentId(row.shipment_id);
          const skuId = row.sku_id ? String(row.sku_id).trim() : null;
          if (shipmentId && skuId) {
            combinationsToCheck.add(`${shipmentId}:${skuId}`);
          }
        });

        console.log(`[階段 3] 需要檢查 ${combinationsToCheck.size} 個組合`);

        if (combinationsToCheck.size > 0) {
          // 分批查詢現有記錄，避免單次查詢過多數據
          const batchSize = 50;
          const combinationsArray = Array.from(combinationsToCheck);

          for (let i = 0; i < combinationsArray.length; i += batchSize) {
            const batch = combinationsArray.slice(i, i + batchSize);
            const shipmentIds = batch.map(combo => combo.split(':')[0]);
            const skuIds = batch.map(combo => combo.split(':')[1]);

            try {
              console.log(`[查詢批次] 批次 ${Math.floor(i/batchSize) + 1}: shipment_ids=${shipmentIds.length}, sku_ids=${skuIds.length}`);

              const { data: existingRecords, error: queryErr } = await supabase
                .from('sales_orders')
                .select('id, shipment_id, sku_id')
                .eq('company_id', companyId)  // 添加公司过滤
                .in('shipment_id', shipmentIds)
                .in('sku_id', skuIds);

              if (queryErr) {
                console.error(`批量查詢現有記錄失敗 (批次 ${Math.floor(i/batchSize) + 1}):`, queryErr);
                console.error(`查詢條件 - shipment_ids:`, shipmentIds, `sku_ids:`, skuIds);
                continue;
              }

              if (existingRecords && existingRecords.length > 0) {
                console.log(`[查詢結果] 批次 ${Math.floor(i/batchSize) + 1} 找到 ${existingRecords.length} 條記錄`);
                existingRecords.forEach(record => {
                  if (record.shipment_id && record.sku_id) {
                    const normalizedShipmentId = normalizeShipmentId(record.shipment_id);
                    const combinationKey = `${normalizedShipmentId}:${record.sku_id}`;
                    existingRecordsMap.set(combinationKey, record.id);
                    console.log(`[記錄映射] ${combinationKey} -> ${record.id}`);
                  }
                });
              } else {
                console.log(`[查詢結果] 批次 ${Math.floor(i/batchSize) + 1} 沒有找到現有記錄`);
              }
            } catch (e) {
              console.warn(`查詢現有記錄批次失敗:`, e);
            }
          }

          console.log(`[階段 3] 總共查詢到 ${existingRecordsMap.size} 個現有記錄用於更新`);
        } else {
          console.log(`[階段 3] 沒有需要檢查的組合，全部為新記錄`);
        }

        // 在內存中處理每一行並匹配數據
        processedRows.forEach(({ index, excelRow, row, payload }) => {
          if (ctl.signal.aborted || !isMountedRef.current) return;

          const skuId = payload.sku_id;

          // 匹配成本數據
          const snapshotCostUnit = costMap.get(skuId) || null;
          console.log(`[SKU成本] ${skuId}: 成本=${snapshotCostUnit}, costMap大小=${costMap.size}, 包含此SKU=${costMap.has(skuId)}`);
          if (!snapshotCostUnit && skuId) {
            console.log(`[SKU成本] ${skuId}: 未找到成本數據，使用 null`);
            // 額外檢查：列出所有可用的SKU成本
            if (costMap.size > 0) {
              console.log(`[SKU成本] 可用的SKU列表:`, Array.from(costMap.keys()).slice(0, 10));
            }
          }

          // 匹配佣金數據並計算
          const saleCommissionRate = commissionMap.get(skuId) || 25.0; // 預設 25%
          if (!commissionMap.has(skuId) && skuId) {
            console.log(`[SKU佣金] ${skuId}: 未找到佣金數據，使用預設 25.0%`);
          }

          const totalAmount = Number(payload.total_amount) || 0;
          const salesCommissionAmount = totalAmount * (saleCommissionRate / 100);

          console.log(`[佣金計算] SKU=${skuId}, 銷售額=${totalAmount}, 佣金率=${saleCommissionRate}%, 佣金金額=${salesCommissionAmount}`);

          // 建立年月字串
          const orderYearMonth = payload.order_datetime ? payload.order_datetime.substring(0, 7) : null;

          // 完成 payload
          payload.snapshot_cost_unit = snapshotCostUnit;
          payload.sale_commission_rate = saleCommissionRate;
          payload.sales_commission_amount = salesCommissionAmount;
          payload.order_year_month = orderYearMonth;

          // 調試：檢查關鍵字段
          console.log(`[Payload-${skuId}] 數量=${payload.quantity}, 單價=${payload.unit_price}, 成本=${payload.snapshot_cost_unit}, 佣金=${payload.sales_commission_amount}`);

          // 決定更新或新增
          const shipmentId = normalizeShipmentId(row.shipment_id);
          const combinationKey = shipmentId && skuId ? `${shipmentId}:${skuId}` : null;

          if (combinationKey && existingRecordsMap.has(combinationKey)) {
            const existingRecordId = existingRecordsMap.get(combinationKey)!;
            const updatePayload = { ...payload, id: existingRecordId };
            recordsToUpdate.push({ id: existingRecordId, data: payload });
            updateEntries.push({ mode: 'update', payload: updatePayload, excelRow });
            console.log(`[記錄匹配] ${combinationKey} -> 更新 ID: ${existingRecordId}`);
          } else {
            recordsToInsert.push(payload);
            insertEntries.push({ mode: 'insert', payload, excelRow });
            if (combinationKey) {
              console.log(`[記錄匹配] ${combinationKey} -> 新增記錄`);
            } else {
              console.log(`[記錄匹配] shipment_id: ${row.shipment_id}, sku_id: ${skuId} -> 缺少必要字段，新增記錄`);
            }
          }

          onProgress(index + 1, totalRows);
        });

        console.log(`[階段 3] 完成：${recordsToUpdate.length} 筆更新，${recordsToInsert.length} 筆新增`);
        onProgress(50, totalRows); // 进度更新：50%

        // 第 4 階段：批量寫入數據庫
        console.log(`[階段 4] 批量寫入：開始批量插入和更新數據庫...`);
        onProgress(60, totalRows); // 进度更新：60%

        // 第 4 階段：三層自動錯誤隔離 (Binary Error Isolation)
        console.log(`Executing batch operations: ${recordsToUpdate.length} updates, ${recordsToInsert.length} inserts`);

        const hardFailedRows: { excelRow: number; message: string }[] = [];
        let writeSuccess = 0;


        const processChunkRecursive = async (entries: WriteEntry[], mode: 'update' | 'insert'): Promise<void> => {
          if (!entries.length || ctl.signal.aborted || !isMountedRef.current) return;

          // 單筆情況：序列單筆重試，記錄失敗行號與錯誤訊息
          if (entries.length === 1) {
            const entry = entries[0];
            try {
              const table = supabase.from('sales_orders');
              const payload = mode === 'update'
                ? [{ ...entry.payload }]
                : [entry.payload];
              const { error, status } = mode === 'update'
                ? await table.upsert(payload)
                : await table.upsert(payload, { onConflict: 'company_id,shipment_id,sku_id,order_datetime' });
              if (error) {
                hardFailedRows.push({ excelRow: entry.excelRow, message: error.message });
                console.error(`[單筆失敗] Excel 行 ${entry.excelRow}:`, error);
              } else {
                writeSuccess++;
                console.log(`[單筆成功] Excel 行 ${entry.excelRow}, status=${status}`);
              }
            } catch (e: any) {
              hardFailedRows.push({ excelRow: entry.excelRow, message: e?.message || 'Unknown error' });
              console.error(`[單筆例外] Excel 行 ${entry.excelRow}:`, e);
            }
            return;
          }

          // 先嘗試整批寫入
          try {
            const table = supabase.from('sales_orders');
            const batchPayload = mode === 'update'
              ? entries.map(e => ({ ...e.payload }))
              : entries.map(e => e.payload);
            const { error, status } = mode === 'update'
              ? await table.upsert(batchPayload)
              : await table.upsert(batchPayload, { onConflict: 'company_id,shipment_id,sku_id,order_datetime' });
            if (!error) {
              writeSuccess += entries.length;
              console.log(`[二分批次成功] ${mode} 批次，筆數=${entries.length}, status=${status}`);
              return;
            }
            console.warn(`[二分批次失敗] ${mode} 批次，筆數=${entries.length}，開始折半重試`, error);
          } catch (e) {
            console.warn(`[二分批次例外] ${mode} 批次，筆數=${entries.length}，開始折半重試`, e);
          }

          // 批次仍失敗 → 折半遞迴
          const mid = Math.floor(entries.length / 2);
          const left = entries.slice(0, mid);
          const right = entries.slice(mid);
          await processChunkRecursive(left, mode);
          await processChunkRecursive(right, mode);
        };

        // 批量更新 - 每批 100 筆，串行執行，失敗批次記錄後繼續
        const updateBatchSize = 100;
        for (let batchStart = 0; batchStart < recordsToUpdate.length; batchStart += updateBatchSize) {
          if (ctl.signal.aborted || !isMountedRef.current) {
            console.log(`[SalesImportEngine-${instanceId.current}-${Date.now()}] 更新處理被中止 (aborted: ${ctl.signal.aborted}, mounted: ${isMountedRef.current})`);
            break;
          }

          const batch = recordsToUpdate.slice(batchStart, batchStart + updateBatchSize);
          const batchIndex = Math.floor(batchStart / updateBatchSize) + 1;
          const entryBatch = updateEntries.slice(batchStart, batchStart + updateBatchSize);

          let retryCount = 0;
          const maxRetries = 3;

          while (retryCount < maxRetries) {
            try {
              console.log(`[更新重試 ${retryCount + 1}/${maxRetries}] 嘗試更新批次 ${batchIndex}: ${batch.length} 筆記錄...`);

              const { error: updateErr, status: updateStatus } = await supabase
                .from('sales_orders')
                .upsert(batch.map(record => ({ ...record.data, id: record.id })));

              if (updateErr) {
                console.error(`Batch update error (attempt ${retryCount + 1}, batch ${batchIndex}):`, updateErr);
                retryCount++;

                if (retryCount < maxRetries) {
                  const delay = Math.pow(2, retryCount) * 1000;
                  console.log(`等待 ${delay}ms 後重試更新批次 ${batchIndex}...`);
                  await new Promise(resolve => setTimeout(resolve, delay));
                  continue;
                }

                // 進入二分遞迴隔離，縮小失敗範圍到單筆
                console.warn(`[更新批次失敗] 進入二分遞迴處理，批次 ${batchIndex}，筆數=${batch.length}`);
                await processChunkRecursive(entryBatch, 'update');
              } else {
                writeSuccess += batch.length;
                console.log(`✅ 批量更新成功: 批次 ${batchIndex}，${batch.length} 筆記錄已更新, status=${updateStatus}`);
              }
              break;
            } catch (e: any) {
              console.error(`Batch update exception (attempt ${retryCount + 1}, batch ${batchIndex}):`, e);

              if (e.message?.includes('Failed to fetch') || e.message?.includes('ERR_INTERNET_DISCONNECTED')) {
                retryCount++;

                if (retryCount < maxRetries) {
                  const delay = Math.pow(2, retryCount) * 1000;
                  console.log(`網路錯誤，等待 ${delay}ms 後重試更新批次 ${batchIndex}...`);
                  await new Promise(resolve => setTimeout(resolve, delay));
                  continue;
                }
              }

              console.warn(`[更新批次例外] 進入二分遞迴處理，批次 ${batchIndex}，筆數=${batch.length}`);
              await processChunkRecursive(entryBatch, 'update');
              break;
            }
          }
        }

        // 批量新增 - 每批 100 筆，串行執行，失敗批次記錄後繼續
        const insertBatchSize = 100;
        for (let batchStart = 0; batchStart < recordsToInsert.length; batchStart += insertBatchSize) {
          if (ctl.signal.aborted || !isMountedRef.current) {
            console.log(`[SalesImportEngine-${instanceId.current}-${Date.now()}] 新增處理被中止 (aborted: ${ctl.signal.aborted}, mounted: ${isMountedRef.current})`);
            break;
          }

          const batch = recordsToInsert.slice(batchStart, batchStart + insertBatchSize);
          const batchIndex = Math.floor(batchStart / insertBatchSize) + 1;
          const entryBatch = insertEntries.slice(batchStart, batchStart + insertBatchSize);

          let retryCount = 0;
          const maxRetries = 3;

          while (retryCount < maxRetries) {
            try {
              console.log(`[新增重試 ${retryCount + 1}/${maxRetries}] 嘗試插入批次 ${batchIndex}: ${batch.length} 筆記錄...`);

              const { error: insertErr, status: insertStatus } = await supabase
                .from('sales_orders')
                .upsert(batch, { onConflict: 'company_id,shipment_id,sku_id,order_datetime' });

              if (insertErr) {
                console.error(`Batch insert error (attempt ${retryCount + 1}, batch ${batchIndex}):`, insertErr);
                retryCount++;

                if (retryCount < maxRetries) {
                  const delay = Math.pow(2, retryCount) * 1000;
                  console.log(`等待 ${delay}ms 後重試插入批次 ${batchIndex}...`);
                  await new Promise(resolve => setTimeout(resolve, delay));
                  continue;
                }

                console.warn(`[新增批次失敗] 進入二分遞迴處理，批次 ${batchIndex}，筆數=${batch.length}`);
                await processChunkRecursive(entryBatch, 'insert');
              } else {
                writeSuccess += batch.length;
                console.log(`✅ 批量新增成功: 批次 ${batchIndex}，${batch.length} 筆記錄已插入, status=${insertStatus}`);
              }
              break;
            } catch (e: any) {
              console.error(`Batch insert exception (attempt ${retryCount + 1}, batch ${batchIndex}):`, e);

              if (e.message?.includes('Failed to fetch') || e.message?.includes('ERR_INTERNET_DISCONNECTED')) {
                retryCount++;

                if (retryCount < maxRetries) {
                  const delay = Math.pow(2, retryCount) * 1000;
                  console.log(`網路錯誤，等待 ${delay}ms 後重試插入批次 ${batchIndex}...`);
                  await new Promise(resolve => setTimeout(resolve, delay));
                  continue;
                }
              }

              console.warn(`[新增批次例外] 進入二分遞迴處理，批次 ${batchIndex}，筆數=${batch.length}`);
              await processChunkRecursive(entryBatch, 'insert');
              break;
            }
          }
        }

        console.log(`[階段 4] 完成：批量寫入數據庫完成，徹底失敗筆數=${hardFailedRows.length}`);

        if (hardFailedRows.length > 0) {
          console.warn('以下 Excel 行號匯入仍失敗：', hardFailedRows.map(r => r.excelRow));
        }

        // 將結果回傳給父元件以供 UI 顯示失敗行列表
        if (onResult) {
          onResult({ hardFailedRows });
        }

        // 所有可行的批次與單筆都已嘗試；將進度拉到 100%，其餘失敗行留在 hardFailedRows 中
        // 最後一個 await 已完成（update/insert 迴圈），onComplete 嚴格置於此處
        onProgress(totalRows, totalRows);
        onComplete();
        console.log(`[SalesImportEngine-${instanceId.current}-${Date.now()}] 處理完成！`);

      } catch (error: any) {
        console.error(`[SalesImportEngine-${instanceId.current}-${Date.now()}] 處理失敗:`, error);
        if (onError) {
          onError({
            message: error?.message || 'Unknown error'
          });
        }
      }
    };

    // 調用run函數開始處理
    console.log(`[SalesImportEngine-${instanceId.current}-${Date.now()}] 📞 調用run函數`);
    run();

    return () => {
      isMountedRef.current = false;
    };
  }, [file, rows, startIndex, currentCompany?.id, isProcessing, isPageFocusRecovery, onError, onProgress, onComplete, language]);

  return null; // SalesImportEngine 不渲染任何內容
};

export default SalesImport;
